/**
 * Language detection utility for browser language detection and mapping
 */

// Supported language codes in the application (BCP 47 standard)
export const SUPPORTED_LANGUAGES = ["en", "de", "da", "nl", "fr", "es", "thai", "zh", "no"];

// Language mapping: browser language codes -> supported language codes
const LANGUAGE_MAP = {
	// German variants
	de: "de",
	"de-DE": "de",
	"de-AT": "de",
	"de-CH": "de",
	"de-LI": "de",
	"de-LU": "de",
	// Legacy non-BCP 47 codes (backward compatibility for existing users)
	ger: "de", // ISO 639-2 code used before BCP 47 migration
	dan: "da", // ISO 639-2 code used before BCP 47 migration
	// Danish variants
	da: "da",
	"da-DK": "da",
	// Dutch variants
	nl: "nl",
	"nl-NL": "nl",
	"nl-BE": "nl",
	// French variants
	fr: "fr",
	"fr-FR": "fr",
	"fr-BE": "fr",
	"fr-CH": "fr",
	"fr-CA": "fr",
	"fr-LU": "fr",
	// Spanish variants
	es: "es",
	"es-ES": "es",
	"es-MX": "es",
	"es-AR": "es",
	"es-CO": "es",
	"es-CL": "es",
	// Thai variants
	th: "thai",
	"th-TH": "thai",
	// Chinese variants
	zh: "zh",
	"zh-CN": "zh",
	"zh-TW": "zh",
	"zh-HK": "zh",
	"zh-SG": "zh",
	// Norwegian variants
	no: "no",
	nb: "no",
	nn: "no",
	"no-NO": "no",
	"nb-NO": "no",
	"nn-NO": "no",
	// English variants (default, but explicit for clarity)
	en: "en",
	"en-US": "en",
	"en-GB": "en",
	"en-AU": "en",
	"en-CA": "en",
	"en-NZ": "en",
};

/**
 * Maps a browser language code to a supported language code
 * @param {string} browserLang - Browser language code (e.g., "de-DE", "fr-FR")
 * @returns {string} Supported language code (e.g., "de", "fr")
 */
export function mapBrowserLanguageToSupported(browserLang) {
	if (!browserLang) return "en";

	// Normalize the language code (lowercase, trim)
	const normalized = browserLang.toLowerCase().trim();

	// If it's already a supported language code, return it as-is
	if (SUPPORTED_LANGUAGES.includes(normalized)) {
		return normalized;
	}

	// Check exact match first (browser codes like "de-DE")
	if (LANGUAGE_MAP[normalized]) {
		return LANGUAGE_MAP[normalized];
	}

	// Check language code without region (e.g., "de" from "de-DE")
	const langCode = normalized.split("-")[0];
	if (LANGUAGE_MAP[langCode]) {
		return LANGUAGE_MAP[langCode];
	}

	// Default to English if no match
	return "en";
}

/**
 * Detects browser language and maps it to a supported language
 * @returns {string} Supported language code
 */
export function detectBrowserLanguage() {
	if (typeof window === "undefined" || !navigator) {
		return "en";
	}

	// Check navigator.languages first (preferred languages array)
	if (navigator.languages && navigator.languages.length > 0) {
		for (const lang of navigator.languages) {
			const mapped = mapBrowserLanguageToSupported(lang);
			if (mapped !== "en" || lang.startsWith("en")) {
				return mapped;
			}
		}
	}

	// Fallback to navigator.language
	if (navigator.language) {
		return mapBrowserLanguageToSupported(navigator.language);
	}

	// Default fallback
	return "en";
}

/**
 * Checks if a language preference exists in localStorage
 * @returns {boolean} True if a language preference exists
 */
export function hasLanguagePreference() {
	if (typeof window === "undefined") {
		return false;
	}

	try {
		const stored = localStorage.getItem("i18nextLng");
		return stored !== null && stored !== undefined && stored !== "";
	} catch (error) {
		console.error("[languageDetection][hasLanguagePreference] - Error checking localStorage:", error);
		return false;
	}
}

/**
 * Checks if language selection dialog has been shown in this session
 * @returns {boolean} True if dialog has been shown
 */
export function hasShownLanguageDialog() {
	if (typeof window === "undefined") {
		return false;
	}

	try {
		return sessionStorage.getItem("languageDialogShown") === "true";
	} catch (error) {
		console.error("[languageDetection][hasShownLanguageDialog] - Error checking sessionStorage:", error);
		return false;
	}
}

/**
 * Marks the language selection dialog as shown in this session
 */
export function markLanguageDialogShown() {
	if (typeof window === "undefined") {
		return;
	}

	try {
		sessionStorage.setItem("languageDialogShown", "true");
	} catch (error) {
		console.error("[languageDetection][markLanguageDialogShown] - Error setting sessionStorage:", error);
	}
}

/**
 * Determines if the language selection dialog should be shown
 * @param {string} currentLanguage - Current i18n language
 * @returns {boolean} True if dialog should be shown
 */
export function shouldShowLanguageDialog(currentLanguage) {
	// Don't show if already shown in this session
	if (hasShownLanguageDialog()) {
		return false;
	}

	// Don't show if user has a language preference stored
	if (hasLanguagePreference()) {
		return false;
	}

	// Show if current language is English (fallback) and no preference exists
	// This means browser detection didn't find a match or user has English browser
	if (currentLanguage === "en") {
		const browserLang = detectBrowserLanguage();
		// If browser language is also English, show dialog to confirm
		// If browser language is different but mapped to English (unsupported), show dialog
		return browserLang === "en" || !SUPPORTED_LANGUAGES.includes(browserLang);
	}

	// If current language is not English but not in supported list, show dialog
	if (!SUPPORTED_LANGUAGES.includes(currentLanguage)) {
		return true;
	}

	return false;
}

